import type { SustainSpan } from "@/lib/track/lane";

/**
 * Une note qui sonne encore après que le doigt s'est levé, prête à dessiner.
 *
 * `toTick` est la fin de la traîne : la levée de la pédale, ou l'extinction de
 * la corde si elle arrive avant. `covered` dit lequel des deux a gagné — 1
 * quand la corde s'est éteinte toute seule, moins quand l'étouffoir est tombé
 * avant la fin.
 */
export type PedalTrail = {
  midi: number;
  laneIdx: number;
  fromTick: number;
  toTick: number;
  covered: number;
};

/**
 * Combien de temps une corde libre sonne encore, en secondes, selon sa hauteur.
 *
 * # Ce n'est pas un choix, c'est une mesure
 *
 * Les durées viennent des échantillons que le studio joue — le Salamander, la
 * couche la plus forte —, relevées au ffprobe : un la0 dure 25,4 s, un do4
 * 15,7 s, un do6 6,1 s, un do7 4,1 s. Elles sont la borne dure de ce que
 * Virtuosa *peut* faire entendre : au-delà, il n'y a plus de son du tout, et
 * une traîne qui continuerait dessinerait un silence.
 *
 * Elles disent aussi la physique, et c'est pourquoi elles se lisent bien : une
 * corde grave est longue, lourde et peu amortie par l'air, elle tient une
 * demi-minute ; une corde aiguë est courte et rigide, elle est éteinte en
 * quelques secondes. C'est exactement ce qu'un pianiste entend quand il tient
 * la pédale sur un accord large — la basse reste, le haut est déjà parti.
 *
 * # Une table plutôt qu'une formule
 *
 * Les relevés ne tombent pas sur une exponentielle : ils sont presque plats
 * jusqu'à do2, puis s'effondrent entre do5 et do7. Une formule ajustée à ça
 * serait plus longue à écrire que la table, et mentirait entre les points.
 * Ceux-ci sont lissés pour rester décroissants — l'éditeur des échantillons a
 * coupé un do5 plus long que le la4 voisin, ce qui n'a pas de sens physique et
 * ne doit pas s'inscrire dans le dessin.
 */
const RING_SECONDS: readonly (readonly [number, number])[] = [
  [21, 25],
  [36, 22],
  [48, 16],
  [60, 15],
  [72, 12],
  [84, 7],
  [96, 4.5],
  [108, 3.5],
];

export function ringSeconds(midi: number): number {
  const last = RING_SECONDS.length - 1;

  if (midi <= RING_SECONDS[0][0]) {
    return RING_SECONDS[0][1];
  }

  if (midi >= RING_SECONDS[last][0]) {
    return RING_SECONDS[last][1];
  }

  for (let i = 1; i <= last; i++) {
    const [highMidi, highSeconds] = RING_SECONDS[i];

    if (midi > highMidi) {
      continue;
    }

    const [lowMidi, lowSeconds] = RING_SECONDS[i - 1];
    const t = (midi - lowMidi) / (highMidi - lowMidi);

    return lowSeconds + (highSeconds - lowSeconds) * t;
  }

  return RING_SECONDS[last][1];
}

/**
 * Où s'arrête la traîne d'une note qui finit à `noteEndTick`, et quelle part
 * de son extinction aura été entendue.
 *
 * Deux façons qu'un son a de s'arrêter, et la première des deux gagne : la
 * pédale se relève et l'étouffoir tombe, ou la corde s'éteint d'elle-même. Le
 * dessin a besoin de savoir laquelle — une corde éteinte s'efface, un
 * étouffoir coupe net —, d'où `covered`.
 *
 * `null` quand la note ne finit pas sous cette pédale : l'étouffoir était déjà
 * retombé, ou n'était pas encore levé.
 */
export function trailEnd(
  noteEndTick: number,
  span: SustainSpan,
  ringTicks: number,
): { toTick: number; covered: number } | null {
  if (noteEndTick < span.from || noteEndTick > span.to || ringTicks <= 0) {
    return null;
  }

  const held = span.to - noteEndTick;

  if (held >= ringTicks) {
    return { toTick: noteEndTick + ringTicks, covered: 1 };
  }

  return { toTick: span.to, covered: held / ringTicks };
}
